import React, { useEffect, useRef, useState } from "react"
import mapboxgl from "mapbox-gl"
import { FeatureCollection, LineString } from "geojson"
import routeData from "../Data/routeData.json"

const MapBoxView: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement | null>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const animationRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(0)
  const currentRouteProgressRef = useRef<number>(0)
  const currentZoomRef = useRef<number>(1)
  const targetZoomRef = useRef<number>(1)
  const frameCounter = useRef<number>(0)

  const currentBearingRef = useRef<number>(0)
  const targetBearingRef = useRef<number>(1)

  const currentPitchRef = useRef<number>(0)
  const targetPitchRef = useRef<number>(0)

  const zoomIncreaseDeltaRef = useRef<number>(0.2)

  const markerRefs = useRef<HTMLDivElement[]>([])
  const headMarkerDivRef = useRef<HTMLDivElement | null>()
  const headMarkerRef = useRef<mapboxgl.Marker | null>()

  const geojsonRef = useRef<FeatureCollection>({
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: []
        },
        properties: {}
      }
    ]
  })

  const routeCoordinates = routeData.map((point: any) => [
    point.Points as { Latitude: number; Longitude: number }[],
    point.ZoomLevel,
    point.SpeedFactor,
    point.CameraLongitude,
    point.CameraLatitude,
    point.Command,
    point.Pitch,
    point.Bearing,
    point.ImageData as {
      ImageName: string
      Position: { Latitude: number; Longitude: number }
    } | null
  ])

  const [playback, setPlayback] = useState("Pause")

  const lastFrameTimeRef = useRef<number>(0)

  const animateLine = (timestamp?: number) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }

    frameCounter.current++

    // // Skip every 2 frame to reduce the animation speed
    // if (frameCounter.current % 2 !== 0) {
    //   animationRef.current = requestAnimationFrame(animateLine)
    //   return
    // }

    lastFrameTimeRef.current = timestamp || performance.now()

    // Interpolate between the current and next route points
    let currentIndex = currentRouteProgressRef.current
    let nextIndex = (currentIndex + 1) % routeCoordinates.length

    // Get the current and next coordinates, zoom levels, and speed factors
    // const [
    //   currentPoints,
    //   currentZoom,
    //   currentSpeed,
    //   currentCameraLng,
    //   currentCameraLat
    // ] = routeCoordinates[currentIndex]

    const [
      nextPoints,
      nextZoom,
      nextSpeed,
      nextCameraLng,
      nextCameraLat,
      command,
      nextPitch,
      nextBearing,
      imageData
    ] = routeCoordinates[currentIndex]

    nextPoints.forEach((point: any) => {
      //console.log("Pushing:>  point: ", point)
      ;(geojsonRef.current.features[0].geometry as LineString).coordinates.push(
        [point.Longitude, point.Latitude]
      )
    })

    const animatedRoute = map.current!.getSource(
      "animated-route"
    ) as mapboxgl.GeoJSONSource
    animatedRoute.setData(geojsonRef.current)

    const camera = map.current!.getFreeCameraOptions()

    // split commands into array of commands by comma

    const commands = command === null ? [] : command.split(",")

    if (commands.includes("Zoom")) {
      console.log("Set Zoom: ", nextZoom)

      // calculate change speed absolute value
      targetZoomRef.current = nextZoom

      const zoomDelta = Math.abs(targetZoomRef.current - currentZoomRef.current)
      zoomIncreaseDeltaRef.current = zoomDelta / 500

      if (zoomIncreaseDeltaRef.current < 0.01) {
        zoomIncreaseDeltaRef.current = 0.01
      }
    }

    if (commands.includes("Pitch")) {
      targetPitchRef.current = nextPitch
    }

    if (commands.includes("Bearing")) {
      targetBearingRef.current = nextBearing
    }

    if (Math.abs(targetZoomRef.current - currentZoomRef.current) > 0.1) {
      const zoomDelta = targetZoomRef.current - currentZoomRef.current
      const zoomSpeed =
        zoomDelta > 0
          ? zoomIncreaseDeltaRef.current
          : zoomIncreaseDeltaRef.current * -1
      currentZoomRef.current += zoomSpeed

      let scale = calculateScale(currentZoomRef.current)

      // if (currentZoomRef.current >= 1) {
      //   scale = 1
      // } else {
      //   scale = currentZoomRef.current
      // }

      markerRefs.current.forEach(markerEl => {
        const baseWidth = 100 // Default width at base zoom
        const baseHeight = 50 // Default height at base zoom

        const wsize = `${baseWidth * scale}px`
        const hsize = `${baseHeight * scale}px`

        markerEl.style.width = wsize
        markerEl.style.height = hsize
        markerEl.style.backgroundSize = "cover" // Ensure the background scales properly
      })
    }

    if (Math.abs(targetBearingRef.current - currentBearingRef.current) > 0) {
      const bearingDelta = targetBearingRef.current - currentBearingRef.current
      const bearingSpeed = bearingDelta > 0 ? 1 : -1
      currentBearingRef.current += bearingSpeed
    }

    if (Math.abs(targetPitchRef.current - currentPitchRef.current) > 0) {
      const pitchDelta = targetPitchRef.current - currentPitchRef.current
      const pitchSpeed = pitchDelta > 0 ? 1 : -1
      currentPitchRef.current += pitchSpeed
    }

    console.log("Current Zoom: ", currentZoomRef.current)
    console.log("Target Zoom: ", targetZoomRef.current)
    console.log("Zoom Delta Speed: ", zoomIncreaseDeltaRef.current)

    if (imageData) {
      const marker: { divElement: HTMLDivElement; marker: mapboxgl.Marker } =
        addMapMarker(
          map.current!,
          imageData.ImageName,
          imageData.Position,
          currentZoomRef.current
        )
      markerRefs.current.push(marker.divElement)
    }

    // headMarkerRef.current!.setLngLat([
    //   nextPoints[0].Longitude,
    //   nextPoints[0].Latitude
    // ])

    // Hide headMarkerRef if the pitch is not 0
    // if (currentPitchRef.current >= 20) {
    //   headMarkerDivRef.current!.style.display = "none"
    // } else {
    //   headMarkerDivRef.current!.style.display = "block"
    // }

    //const altitudeFactor = Math.cos((60 * Math.PI) / 180)

    if (nextCameraLng != 0) {
      const altitude = 1000000 / currentZoomRef.current

      const adjustedAltitudeKm =
        (altitude / 1000) * Math.cos((currentPitchRef.current * Math.PI) / 180)

      const RADIUS_EARTH_KM = 6371 // Earth's radius in kilometers
      //const altitudeKm = altitude / 1000 // Convert altitude to kilometers

      // Offset distance based on altitude and pitch
      const offsetDistanceKm =
        adjustedAltitudeKm * Math.tan((currentPitchRef.current * Math.PI) / 180)

      const finalAltitude = adjustedAltitudeKm * 1000

      const latOffset =
        (offsetDistanceKm / RADIUS_EARTH_KM) *
        (180 / Math.PI) *
        Math.cos((currentBearingRef.current * Math.PI) / 180) // Latitude shift
      const lngOffset =
        (offsetDistanceKm /
          (RADIUS_EARTH_KM * Math.cos((nextCameraLat * Math.PI) / 180))) *
        (180 / Math.PI) *
        Math.sin((currentBearingRef.current * Math.PI) / 180) // Longitude shift

      const adjustedCameraLat = nextCameraLat - latOffset
      const adjustedCameraLng = nextCameraLng - lngOffset

      // const effectiveAltitudeKm = Math.sqrt(
      //   Math.pow(altitudeKm, 2) + Math.pow(offsetDistanceKm, 2)
      // )

      // const adjustedAltitude = effectiveAltitudeKm * 1000

      // Adjust camera position based only on latitude
      // const adjustedCameraLat =
      //   nextCameraLat -
      //   (offsetDistanceKm / RADIUS_EARTH_KM) * (180 / Math.PI) -
      //   latOffset // Latitude shift

      // const adjustedCameraLng = nextCameraLng - lngOffset // Longitude shift

      camera.position = mapboxgl.MercatorCoordinate.fromLngLat(
        { lng: adjustedCameraLng, lat: adjustedCameraLat },
        finalAltitude
      )

      // const baseAltitude = 1000; // Reference altitude
      // const scale = Math.max(0.5, baseAltitude / altitude);

      camera.setPitchBearing(currentPitchRef.current, currentBearingRef.current)
      //camera.lookAtPoint({ lng: nextCameraLng, lat: nextCameraLat })

      // camera.setPitchBearing(60, -30)
      // Set where the camera is looking at (e.g., look at the current interpolated point)

      // Apply the updated camera options to the map
      map.current!.setFreeCameraOptions(camera)
    }

    if (currentRouteProgressRef.current == routeCoordinates.length - 2) {
      console.log("Finished animation")
      cancelAnimationFrame(animationRef.current!)
      return
    }

    currentRouteProgressRef.current++

    // Request the next frame
    animationRef.current = requestAnimationFrame(animateLine)
  }

  const handlePlaybackToggle = () => {
    if (playback === "Pause") {
      cancelAnimationFrame(animationRef.current!)
      setPlayback("Play")
    } else {
      startTimeRef.current = performance.now()
      ;(geojsonRef.current.features[0].geometry as LineString).coordinates = [] // Reset the route
      animateLine(performance.now())
      setPlayback("Pause")
    }
  }

  useEffect(() => {
    mapboxgl.accessToken =
      "pk.eyJ1Ijoic2llcnVhIiwiYSI6ImNsb3l0M2ViZzA2aHgybHA4Z2xjZzIzNXoifQ.NMXQZnHkhW99mJDtNiVV0A"

    // Create the map
    map.current = new mapboxgl.Map({
      container: mapContainer.current!,
      style: "mapbox://styles/sierua/cm40fl5dw000q01qzg9jb9qbn",
      center: [28.6139, 77.209],
      zoom: 10,
      pitch: 0, // Tilt the map for 3D effect
      bearing: 0 // Rotate the map for a better perspective
    })

    var currentMap = map.current

    currentMap.on("style.load", () => {
      currentMap.addSource("mapbox-dem", {
        type: "raster-dem",
        url: "mapbox://mapbox.mapbox-terrain-dem-v1",
        tileSize: 512,
        maxzoom: 14
      })
      currentMap.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 })
    })

    map.current.on("load", () => {
      // Add an empty GeoJSON source for the animated route
      if (!currentMap.getSource("animated-route")) {
        currentMap.addSource("animated-route", {
          type: "geojson",
          data: geojsonRef.current
        })
      }

      // Add the animated route layer
      currentMap.addLayer({
        id: "animated-route-layer",
        type: "line",
        source: "animated-route",
        layout: {
          "line-join": "round",
          "line-cap": "round"
        },
        paint: {
          "line-color": "#FF5733",
          "line-width": 5
          //"line-dasharray": [0.1, 2]
        }
      })

      // Create a custom marker element

      // const headMarker = addMapMarker(currentMap, "moto-right.png", {
      //   Latitude: 28.6139,
      //   Longitude: 77.209
      // })

      // headMarkerDivRef.current = headMarker.divElement
      // headMarkerRef.current = headMarker.marker

      // headMarkerDivRef.current.style.width = "70px"
      // headMarkerDivRef.current.style.height = "90px"

      // Start animation

      setTimeout(() => {
        console.log("Starting animation")
        animateLine(performance.now())
      }, 2000)

      // Add visibility change listener to handle tab switching
      // document.addEventListener("visibilitychange", () => {
      //   startTimeRef.current = 0
      //   ;(geojsonRef.current.features[0].geometry as LineString).coordinates =
      //     []
      //   if (playback === "Pause") {
      //     animateLine(performance.now())
      //   }
      // })
    })

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  return (
    <div
      className="map-container"
      style={{ position: "relative", height: "100vh" }}
    >
      <div ref={mapContainer} style={{ height: "100%" }} />
      <button
        onClick={handlePlaybackToggle}
        style={{
          position: "absolute",
          display: "none",
          top: "20px",
          left: "20px",
          //zIndex: 1,
          padding: "10px",
          backgroundColor: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer"
        }}
      >
        {playback}
      </button>
    </div>
  )
}

export default MapBoxView

function addMapMarker(
  currentMap: mapboxgl.Map,
  imageName: string,
  position: { Latitude: number; Longitude: number },
  zoomLevel: number
): { divElement: HTMLDivElement; marker: mapboxgl.Marker } {
  const markerEl = document.createElement("div")
  markerEl.style.backgroundImage = `url(images/${imageName})` // Use the imported image

  let scale = calculateScale(zoomLevel)

  const baseWidth = 100
  const baseHeight = 50

  const wsize = `${baseWidth * scale}px`
  const hsize = `${baseHeight * scale}px`

  markerEl.style.width = wsize
  markerEl.style.height = hsize
  markerEl.style.backgroundSize = "cover"
  markerEl.style.borderRadius = "20%" // Optional: Make it circular
  // Add the marker to the map
  const marker = new mapboxgl.Marker(markerEl)
    .setLngLat([position.Longitude, position.Latitude])
    .addTo(currentMap)

  return { divElement: markerEl, marker }
}
function calculateScale(zoomLevel: number) {
  let scale = 1.2

  if (zoomLevel < 10) {
    scale = 1.4
  }

  if (zoomLevel < 2) {
    scale = 1.6
  }

  if (zoomLevel < 0.5) {
    scale = 0
  }
  return scale
}
